import clsx from 'clsx';
import { isEqual } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { Editor, type EditorOptions } from '@tiptap/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Modal } from '@flintmint/web-shared/components/modal';
import { Loader } from '@flintmint/web-shared/components/loader';
import { CortexProcessing } from '@/components/cortex-processing';
import { CortexPreviewActions } from './parts/cortex-preview-actions';
import { PreviewEditorHolder } from '@/components/preview-editor-holder';
import { SimpleDocumentHeader } from '@/components/simple-document-header';
import { SuggestionsNavigation } from '@/components/suggestion-navigation';

import { useItemActions } from '@/hooks/use-item-actions';
import { useLeaveOnArchived } from '@/hooks/use-leave-on-archived';
import { useUserSource, useUserSourceWriter } from '@/hooks/rxdb-models';
import { useCloseConfirmation } from '@flintmint/web-shared/hooks/use-close-confirmation';
import {
  useReviewActions,
  toPlainPendingReview,
  type PendingReview,
  type PersistedDocumentWriter,
} from '@/hooks/llm/use-llm-agent';

import { appRoutes } from '@/constants/routes';
import { getEditorHTML } from '@/utils/editor-utils';

import styles from './cortex-preview-modal.module.scss';
import { conditionalSpread } from '@flintmint/web-shared/utils/helper-utils';

export const cortexPreviewScrollParent = 'cortex-scroller';

export const CortexPreviewModal = (props: CortexPreviewModalProps) => {
  const { open, onClose, userSourceId, userSourceAttached, onReplaceWithNew, toggleAttachment, noteVariant } = props;

  const [ready, setReady] = useState(false);
  const [content, setContent] = useState('');
  const [inEditMode, setInEditMode] = useState(false);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [contentBeforeProcessing, setContentBeforeProcessing] = useState('');

  const navigate = useNavigate();
  const { configureDestructiveOptions } = useItemActions();

  const { data: userSource, isLoading } = useUserSource(userSourceId);
  const { mutate: upsertUserSource } = useUserSourceWriter(userSource, userSourceId, { createIfMissing: false });
  const persistUserSourceDocumentPatch: PersistedDocumentWriter = async ({ content, pendingReview }) => {
    await upsertUserSource({
      ...(content !== undefined && { content }),
      ...(pendingReview !== undefined && { pendingReview }),
    });
  };

  const llmRequestId = userSource?.id ?? userSourceId ?? '';
  const documentPendingReview = (userSource?.pendingReview ?? null) as PendingReview | null;
  const pendingReview = useMemo(
    () => (documentPendingReview ? toPlainPendingReview(documentPendingReview) : null),
    [documentPendingReview]
  );
  const hasPendingReview = (pendingReview?.suggestions.length ?? 0) > 0;
  const review = useReviewActions({
    requestId: llmRequestId,
    editor,
    pendingReview,
    persistDocumentPatch: persistUserSourceDocumentPatch,
  });

  const hasUnsavedChanges = useMemo(() => {
    return inEditMode && !isEqual(content, userSource?.content);
  }, [content, inEditMode, userSource]);

  const handleClose = () => {
    onClose();
    setContent('');
    setInEditMode(false);
    setContentBeforeProcessing('');
    setIsCloseConfirmationOpen(false);
    setReady(false);
  };

  const { isCloseConfirmationOpen, setIsCloseConfirmationOpen, showConfirmation, renderAlert } = useCloseConfirmation({
    hostOpen: open,
    hasUnsavedChanges,
    onClose: handleClose,
    title: 'Close the Cortex Item?',
    message: 'Are you sure you want to close the Cortex Item? You have unsaved changes.',
  });

  useLeaveOnArchived(userSource, onClose);

  useEffect(() => {
    if (!isLoading && userSource) {
      setContent(userSource.content);
      setReady(true);
    }
  }, [isLoading, userSource]);

  useEffect(() => {
    if (userSource?.syncStatus === 'processing') {
      setContentBeforeProcessing(userSource.content);
    }
  }, [userSource?.syncStatus, userSource?.content]);

  const onUpdate: EditorOptions['onUpdate'] = useCallback(({ editor }) => {
    setContent(getEditorHTML(editor as Editor));
  }, []);

  const isNoteScopedUserSource = userSource?.availabilityScope === 'note';

  const menu = [
    {
      name: 'Open in Cortex',
      icon: 'link-square-02',
      onClick: () => {
        navigate(`${appRoutes.cortex}/${userSource?.id}`);
      },
    },
    ...conditionalSpread(
      [
        {
          type: 'divider',
        },
        {
          danger: userSourceAttached,
          onClick: () => toggleAttachment(userSource?.id!, userSourceAttached),
          icon: userSourceAttached ? 'file-remove' : 'file-add',
          name: userSourceAttached ? 'Detach From Note' : 'Attach to Note',
        },
      ],
      !isNoteScopedUserSource
    ),
    ...conditionalSpread(
      [
        {
          type: 'divider',
        },
        {
          onClick: () => upsertUserSource({ availabilityScope: 'global' }),
          icon: 'globe-02',
          name: 'Make Globally Available',
        },
      ],
      isNoteScopedUserSource
    ),
    ...configureDestructiveOptions([{ ...userSource?.toJSON()!, type: 'userSources' }], { onDone: onClose }),
  ];

  return (
    <>
      {renderAlert()}
      <Modal
        freeForm
        open={open}
        decoupledScroll
        maxWidth={1060}
        closeOnEscape={false}
        onClose={handleClose}
        id={cortexPreviewScrollParent}
        onClickOutside={showConfirmation}
        data-testid="cortex-preview-modal"
        holderClassName={clsx(styles.cortexPreviewModalHolder, {
          [styles.inEditMode]: inEditMode,
        })}
        closeOnClickOutside={!hasUnsavedChanges}
      >
        {!!userSource && !isLoading ? (
          <div className={styles.cortexPreviewModal}>
            <SimpleDocumentHeader
              menu={menu}
              size="small"
              editor={editor}
              closeFromRight
              data={userSource}
              onClose={handleClose}
              renderAlert={renderAlert}
              documentName={userSource.name}
              data-testid="cortex-preview-header"
              className={styles.cortexPreviewHeader}
              closeWithAlert={hasUnsavedChanges && !isCloseConfirmationOpen}
              onTagsUpdate={(tagIds) => upsertUserSource({ tagIds })}
            />
            <CortexPreviewActions
              content={content}
              onClose={handleClose}
              userSource={userSource}
              inEditMode={inEditMode}
              noteVariant={noteVariant}
              renderAlert={renderAlert}
              setInEditMode={setInEditMode}
              isAttachedToDoc={userSourceAttached}
              saveBlockedByReview={hasPendingReview}
              onReplaceWithNew={onReplaceWithNew}
              hasUnsavedChanges={hasUnsavedChanges}
              isCloseConfirmationOpen={isCloseConfirmationOpen}
              onCancel={() => {
                setInEditMode(false);
                setContent(contentBeforeProcessing ? contentBeforeProcessing : userSource.content!);
              }}
            />
            {ready && (
              <PreviewEditorHolder
                inEditMode={inEditMode}
                onSetEditor={setEditor}
                editorProps={{
                  content,
                  onUpdate,
                  editorType: 'cortex',
                  documentId: userSource.id,
                  processing: userSource.syncStatus === 'processing' || userSource.syncStatus === 'error',
                  persistDocumentPatch: persistUserSourceDocumentPatch,
                  scrollParentId: cortexPreviewScrollParent,
                  placeholder: 'No content to preview. Start adding content to your cortex item',
                  processingView: <CortexProcessing userSource={userSource} />,
                }}
              />
            )}
            {!!review?.suggestions.length && editor && review ? (
              <SuggestionsNavigation
                editor={editor}
                review={review}
                className={styles.suggestions}
                data-testid="cortex-prompt-box"
              />
            ) : null}
          </div>
        ) : (
          <Loader absolute data-testid="loader" />
        )}
      </Modal>
    </>
  );
};

interface CortexPreviewModalProps {
  open: boolean;
  onClose: () => void;
  noteVariant?: string;
  userSourceId?: string;
  userSourceAttached: boolean;
  toggleAttachment: (id: string, attached: boolean) => void;
  onReplaceWithNew: (id: string, newId: string) => void;
}
